<template>
  <div class="hr-page member-list-page">
    <header class="console-page-head">
      <div class="console-page-copy">
        <p class="console-kicker">조직</p>
        <h2 class="console-title">고객관리</h2>
      </div>
      <div class="console-head-side">
        <span class="console-count">{{ totalCount.toLocaleString() }}건</span>
      </div>
    </header>

    <div v-if="canSeeServiceTabs" class="member-service-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="member-service-tab member-service-tab--service"
        :class="{ active: serviceTab === 'service' }"
        :aria-selected="serviceTab === 'service'"
        @click="setServiceTab('service')"
      >
        <span class="tab-text">
          <span class="tab-label">임금·회계 이용</span>
          <span class="tab-desc">담당자가 배정된 기관</span>
        </span>
        <span class="tab-count">{{ tabCounts.service }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="member-service-tab member-service-tab--none"
        :class="{ active: serviceTab === 'none' }"
        :aria-selected="serviceTab === 'none'"
        @click="setServiceTab('none')"
      >
        <span class="tab-text">
          <span class="tab-label">서비스 미이용</span>
          <span class="tab-desc">담당자가 없는 기관</span>
        </span>
        <span class="tab-count">{{ tabCounts.none }}</span>
      </button>
    </div>

    <div class="hr-body">
    <div class="hr-main-layout">

      <div class="content-card">
        <!-- 검색/필터 -->
        <div class="search-area">
            <v-row align="center" dense>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="searchText"
                  placeholder="시설명, ID, 시설코드로 검색..."
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  density="compact"
                  hide-details
                  clearable
                  @keyup.enter="fetchMemberList"
                  @click:clear="onClearSearch"
                />
              </v-col>
              <v-col cols="6" md="3">
                <v-select
                  v-model="selectedBizGubun"
                  :items="bizGubunOptions"
                  item-title="bg_name"
                  item-value="bg_value"
                  label="사업구분"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @update:modelValue="fetchMemberList"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend v-if="item.raw.bg_color">
                        <span class="color-dot" :style="{ background: item.raw.bg_color }"></span>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="6" md="2">
                <v-select
                  v-model="selectedStatus"
                  :items="statusOptions"
                  item-title="text"
                  item-value="value"
                  label="상태"
                  variant="outlined"
                  density="compact"
                  hide-details
                  @update:modelValue="fetchMemberList"
                />
              </v-col>
              <v-col cols="12" md="2" class="d-flex gap-2">
                <v-btn variant="flat" class="btn-search" @click="fetchMemberList" block>
                  <v-icon size="small" class="mr-1">mdi-magnify</v-icon>
                  검색
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- 테이블 -->
          <v-data-table
            :headers="headers"
            :items="memberList"
            :loading="loading"
            :items-per-page="itemsPerPage"
            :page="currentPage"
            hover
            class="member-table"
          >
            <template v-slot:item.index="{ index }">
              <span class="row-num">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
            </template>

            <template v-slot:item.m_name="{ item }">
              <div class="member-name clickable" @click="openMemberPopup(item)">
                <span class="name-text">
                  <span class="name-label">{{ item.m_name }}</span>
                  <span v-if="item.m_signup_source === 'WEB'" class="source-chip source-chip--mall">쇼핑몰</span>
                </span>
                <span class="code-text">{{ item.m_longterm_code }}</span>
              </div>
            </template>

            <template v-slot:item.m_biz_gubun="{ item }">
              <div class="biz-chips">
                <v-chip 
                  v-for="biz in parseBizGubun(item.m_biz_gubun)" 
                  :key="biz"
                  :color="getBizGubunColor(biz)" 
                  size="x-small" 
                  label 
                  variant="flat"
                  class="biz-chip"
                >
                  {{ biz }}
                </v-chip>
                <span v-if="!item.m_biz_gubun">-</span>
              </div>
            </template>

            <template v-slot:item.wage_manager="{ item }">
              <div class="assignee-cell" :title="formatAssigneeTitle(item.wage_assignees)">
                <template v-if="(item.wage_assignees || []).length">
                  <span
                    v-for="name in visibleAssigneeNames(item.wage_assignees)"
                    :key="`w-${item.m_code}-${name}`"
                    class="assignee-name-chip assignee-name-chip--wage"
                  >{{ name }}</span>
                  <span
                    v-if="hiddenAssigneeCount(item.wage_assignees) > 0"
                    class="assignee-more"
                  >+{{ hiddenAssigneeCount(item.wage_assignees) }}</span>
                </template>
                <span v-else class="no-manager">-</span>
              </div>
            </template>

            <template v-slot:item.account_manager="{ item }">
              <div class="assignee-cell" :title="formatAssigneeTitle(item.account_assignees)">
                <template v-if="(item.account_assignees || []).length">
                  <span
                    v-for="name in visibleAssigneeNames(item.account_assignees)"
                    :key="`a-${item.m_code}-${name}`"
                    class="assignee-name-chip assignee-name-chip--account"
                  >{{ name }}</span>
                  <span
                    v-if="hiddenAssigneeCount(item.account_assignees) > 0"
                    class="assignee-more"
                  >+{{ hiddenAssigneeCount(item.account_assignees) }}</span>
                </template>
                <span v-else class="no-manager">-</span>
              </div>
            </template>

            <template v-slot:item.m_boss_name="{ item }">
              <span>{{ item.m_boss_name || '-' }}</span>
            </template>

            <template v-slot:item.m_wdate="{ item }">
              <span class="joined-date">{{ formatJoinedDate(item.m_wdate) }}</span>
            </template>

            <template v-slot:item.last_activity="{ item }">
              <div class="last-activity" :title="formatActivityFull(item.last_activity_at)">
                <span class="activity-label">{{ item.last_activity_label || '-' }}</span>
                <span v-if="item.last_activity_at" class="activity-time">{{ formatActivityTime(item.last_activity_at) }}</span>
              </div>
            </template>

            <template v-slot:item.m_status="{ item }">
              <span class="status-dot" :class="item.m_status === 1 ? 'active' : 'inactive'"></span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="action-btns">
                <v-btn
                  size="small"
                  variant="flat"
                  class="btn-edit"
                  @click.stop="openEditDialog(item)"
                >
                  수정
                </v-btn>
                <v-btn
                  v-if="canDeleteMember"
                  size="small"
                  variant="text"
                  class="btn-delete"
                  @click.stop="confirmDelete(item)"
                >
                  삭제
                </v-btn>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="empty-message">
                <v-icon size="64" color="grey-lighten-1">mdi-domain-off</v-icon>
                <p>{{ emptyListMessage }}</p>
              </div>
            </template>

            <template v-slot:bottom>
              <TablePagination
                v-model:current-page="currentPage"
                v-model:items-per-page="itemsPerPage"
                :total-count="totalCount"
                :total-pages="totalPages"
                @update:current-page="onPageChange"
                @update:items-per-page="onItemsPerPageChange"
              />
            </template>
          </v-data-table>
      </div><!-- /content-card -->
    </div><!-- /hr-main-layout -->
    </div><!-- /hr-body -->

    <!-- 시설 정보 수정 다이얼로그 (바로 편집) -->
    <v-dialog v-model="detailDialog" max-width="720px" persistent>
      <v-card class="detail-dialog member-edit-dialog">
        <v-card-title class="dialog-header">
          <v-icon class="mr-2">mdi-pencil-outline</v-icon>
          <span>시설 정보 수정</span>
          <v-chip
            :color="editForm.m_status === 1 ? 'success' : 'grey'"
            size="x-small"
            class="ml-2"
          >
            {{ editForm.m_status === 1 ? '활성' : '비활성' }}
          </v-chip>
          <button
            type="button"
            class="member-edit-dialog__close"
            :disabled="editLoading"
            aria-label="닫기"
            @click="closeEditDialog"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text v-if="editForm" class="member-edit-body">
          <LoadingOverlay :show="detailLoading || editLoading" :text="editLoading ? '저장 중...' : '불러오는 중...'" />
          <div class="edit-grid">
            <div class="edit-section">
              <h4 class="section-title">기본 정보</h4>
              <div class="edit-row">
                <span class="label">시설명 *</span>
                <input
                  v-model="editForm.m_name"
                  type="text"
                  class="native-input"
                  placeholder="시설명을 입력하세요"
                />
              </div>
              <div class="edit-row">
                <span class="label">시설코드</span>
                <input
                  v-model="editForm.m_longterm_code"
                  type="text"
                  class="native-input disabled"
                  disabled
                />
              </div>
              <div class="edit-row edit-row--assignee">
                <span class="label">사업구분 *</span>
                <div class="assignee-multi">
                  <label
                    v-for="biz in bizGubunList"
                    :key="biz.bg_name"
                    class="assignee-check"
                    :class="{ disabled: isBizGubunDisabled(biz.bg_exclusive_group) }"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedBizGubunSet.has(biz.bg_name)"
                      :disabled="isBizGubunDisabled(biz.bg_exclusive_group)"
                      @change="toggleBizGubun(biz.bg_name, biz.bg_exclusive_group)"
                    />
                    <span>{{ biz.bg_name }}</span>
                  </label>
                </div>
              </div>
              <div class="edit-row">
                <span class="label">사업자번호</span>
                <input
                  v-model="editForm.m_biz_num"
                  type="text"
                  class="native-input"
                  placeholder="000-00-00000"
                />
              </div>
              <div class="edit-row">
                <span class="label">현원/정원</span>
                <div class="input-group">
                  <input
                    v-model.number="editForm.m_hyunwon"
                    type="number"
                    class="native-input small"
                    placeholder="현원"
                    min="0"
                  />
                  <span class="separator">/</span>
                  <input
                    v-model.number="editForm.m_jungwon"
                    type="number"
                    class="native-input small"
                    placeholder="정원"
                    min="0"
                  />
                  <span class="unit">명</span>
                </div>
              </div>
              <div class="edit-row">
                <span class="label">상태</span>
                <div v-if="canDeleteMember" class="status-buttons">
                  <button
                    type="button"
                    :class="['status-btn', { active: editForm.m_status === 1 }]"
                    @click="editForm.m_status = 1"
                  >활성</button>
                  <button
                    type="button"
                    :class="['status-btn inactive', { active: editForm.m_status === 0 }]"
                    @click="editForm.m_status = 0"
                  >비활성</button>
                </div>
                <span v-else class="assignee-readonly">
                  {{ editForm.m_status === 1 ? '활성' : '비활성' }}
                </span>
              </div>
            </div>

            <div class="edit-section">
              <h4 class="section-title">대표자 정보</h4>
              <div class="edit-row">
                <span class="label">대표자</span>
                <input
                  v-model="editForm.m_boss_name"
                  type="text"
                  class="native-input"
                  placeholder="대표자명"
                />
              </div>
              <div class="edit-row">
                <span class="label">연락처</span>
                <input
                  v-model="editForm.m_boss_phone"
                  type="tel"
                  class="native-input"
                  placeholder="010-0000-0000"
                />
              </div>
              <div class="edit-row">
                <span class="label">이메일</span>
                <input
                  v-model="editForm.m_boss_email"
                  type="email"
                  class="native-input"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <!-- 담당 배정: 관리자(90+)만 체크박스 편집. 매니저는 배정 현황만 표시 -->
            <div class="edit-section full">
              <h4 class="section-title">담당 권한</h4>
              <template v-if="isAdmin">
                <div class="edit-row edit-row--assignee">
                  <span class="label">임금담당</span>
                  <div class="assignee-multi">
                    <label
                      v-for="manager in managerList"
                      :key="`wage-${manager.u_idx}`"
                      class="assignee-check"
                    >
                      <input
                        type="checkbox"
                        :value="manager.u_idx"
                        :checked="wageAssigneeIds.includes(manager.u_idx)"
                        @change="onAssigneeCheck('wage', manager.u_idx, $event)"
                      />
                      <span>{{ manager.u_name }}</span>
                    </label>
                    <span v-if="managerLoading" class="no-manager">불러오는 중...</span>
                    <span v-else-if="!managerList.length" class="no-manager">담당자 후보 없음</span>
                  </div>
                </div>
                <div class="edit-row edit-row--assignee">
                  <span class="label">회계담당</span>
                  <div class="assignee-multi">
                    <label
                      v-for="manager in managerList"
                      :key="`account-${manager.u_idx}`"
                      class="assignee-check"
                    >
                      <input
                        type="checkbox"
                        :value="manager.u_idx"
                        :checked="accountAssigneeIds.includes(manager.u_idx)"
                        @change="onAssigneeCheck('account', manager.u_idx, $event)"
                      />
                      <span>{{ manager.u_name }}</span>
                    </label>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="edit-row">
                  <span class="label">임금담당</span>
                  <span class="assignee-readonly">{{ editForm.wage_assignee_names || '-' }}</span>
                </div>
                <div class="edit-row">
                  <span class="label">회계담당</span>
                  <span class="assignee-readonly">{{ editForm.account_assignee_names || '-' }}</span>
                </div>
                <p class="assignee-hint">담당자 배정은 관리자만 변경할 수 있습니다.</p>
              </template>
              <div class="edit-row">
                <span class="label">주소</span>
                <input
                  v-model="editForm.m_addr"
                  type="text"
                  class="native-input"
                  placeholder="주소를 입력하세요"
                />
              </div>
              <div class="edit-row">
                <span class="label">전화번호</span>
                <input
                  v-model="editForm.m_tell"
                  type="tel"
                  class="native-input"
                  placeholder="02-0000-0000"
                />
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn
            variant="text"
            class="btn-cancel"
            :disabled="editLoading"
            @click="closeEditDialog"
          >
            취소
          </v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            class="btn-save"
            :loading="editLoading"
            :disabled="detailLoading"
            @click="saveEdit"
          >
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 삭제 확인 다이얼로그 -->
    <DeleteConfirmDialog
      v-model="deleteDialog"
      title="기관 계정 삭제"
      :target-name="deleteTarget?.m_name || ''"
      message="계정을 삭제합니다. 같은 아이디·기관기호로 다시 가입할 수는 있습니다."
      warning="되돌릴 수 없습니다. 삭제된 고객은 복원할 수 없으니 신중히 진행해 주세요."
      :loading="deleteLoading"
      @confirm="deleteMember"
    />
  </div><!-- /hr-page -->
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { memberApi, userApi } from '@/services/api';
import TablePagination from '@/components/common/TablePagination.vue';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

const store = useStore();

const currentUser = computed(() => store.getters.currentUser);
const userOrgCode = computed(() => store.getters.orgCode);
const userRoleLevel = computed(() => store.getters.roleLevel);
const actualRoleLevel = computed(() => store.getters.actualRoleLevel ?? store.getters.roleLevel);
/** ADMIN+(90+) — 담당 배정·기관 삭제·활성/비활성. 임금·회계 담당(배정)은 불가 */
const isAdmin = computed(() => Number(actualRoleLevel.value) >= 90);
const canDeleteMember = computed(() => isAdmin.value);
/** 관리자(80+)만 임금·회계 이용 / 서비스 미이용 탭 */
const canSeeServiceTabs = computed(() => {
  const level = store.getters.isImpersonating
    ? Number(userRoleLevel.value)
    : Number(actualRoleLevel.value);
  return level >= 80;
});
// 상태
const loading = ref(false);
const memberList = ref<any[]>([]);
const totalCount = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(20);
const searchText = ref('');
const selectedBizGubun = ref('');
const selectedStatus = ref<number | string>(1); // 기본값: 활성
const serviceTab = ref<'service' | 'none'>('service');
const tabCounts = ref({ service: 0, none: 0 });
const bizGubunList = ref<any[]>([]);
const bizGubunColorMap = ref<Record<string, string>>({});

const emptyListMessage = computed(() => {
  if (!canSeeServiceTabs.value) return '등록된 기관이 없습니다.';
  return serviceTab.value === 'service'
    ? '임금·회계 서비스를 이용 중인 기관이 없습니다.'
    : '서비스를 이용하지 않는 기관이 없습니다.';
});

// 사업구분 옵션 (전체 포함)
const bizGubunOptions = computed(() => {
  const options = [{ bg_name: '전체', bg_value: '', bg_color: '' }];
  return options.concat(bizGubunList.value.map(item => ({
    bg_name: item.bg_name,
    bg_value: item.bg_name,
    bg_color: item.bg_color,
    bg_exclusive_group: item.bg_exclusive_group
  })));
});

// 적용된 필터 개수
const filterCount = computed(() => {
  let count = 0;
  if (selectedBizGubun.value) count++;
  if (selectedStatus.value !== '') count++;
  if (searchText.value) count++;
  return count;
});

// 필터 적용 여부
const hasActiveFilters = computed(() => filterCount.value > 0);
const detailDialog = ref(false);
const selectedMember = ref<any>(null);

// 상세/수정 다이얼로그
const detailLoading = ref(false);
const editLoading = ref(false);
const editFormRef = ref<any>(null);
const editForm = ref<any>({});


// 담당자 목록 (상세 다이얼로그에서 사용)
const managerLoading = ref(false);
const managerList = ref<any[]>([]);
const wageAssigneeIds = ref<number[]>([]);
const accountAssigneeIds = ref<number[]>([]);

const toggleAssignee = (kind: 'wage' | 'account', uIdx: number, checked: boolean) => {
  const target = kind === 'wage' ? wageAssigneeIds : accountAssigneeIds;
  if (checked) {
    if (!target.value.includes(uIdx)) target.value = [...target.value, uIdx];
  } else {
    target.value = target.value.filter((id) => id !== uIdx);
  }
};

const onAssigneeCheck = (kind: 'wage' | 'account', uIdx: number, e: Event) => {
  const el = e.target as HTMLInputElement;
  toggleAssignee(kind, uIdx, !!el?.checked);
};

const syncAssigneeIdsFromForm = () => {
  wageAssigneeIds.value = (editForm.value.wage_assignees || []).map((a: any) => Number(a.u_idx));
  accountAssigneeIds.value = (editForm.value.account_assignees || []).map((a: any) => Number(a.u_idx));
};

// 멤버 삭제
const deleteDialog = ref(false);
const deleteTarget = ref<any>(null);
const deleteLoading = ref(false);

const statusOptions = [
  { text: '전체', value: '' },
  { text: '활성', value: 1 },
  { text: '비활성', value: 0 }
];

const headers = [
  { title: 'No', key: 'index', sortable: false, align: 'center' as const },
  { title: '시설명', key: 'm_name', sortable: true },
  { title: '사업구분', key: 'm_biz_gubun', sortable: true },
  { title: '임금담당', key: 'wage_manager', sortable: false },
  { title: '회계담당', key: 'account_manager', sortable: false },
  { title: '대표자', key: 'm_boss_name', sortable: true },
  { title: '가입일', key: 'm_wdate', sortable: true },
  { title: '최근 행동', key: 'last_activity', sortable: false },
  { title: '상태', key: 'm_status', sortable: true, align: 'center' as const },
  { title: '', key: 'actions', sortable: false }
];

const formatJoinedDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const s = String(dateStr);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const formatActivityTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const date = new Date(String(dateStr).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(dateStr).slice(0, 16);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hm = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  if (days === 0) return `오늘 ${hm}`;
  if (days === 1) return '어제';
  if (days < 7) return `${days}일 전`;
  return String(dateStr).slice(0, 10);
};

const formatActivityFull = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const s = String(dateStr);
  return s.length >= 16 ? s.slice(0, 16) : s;
};

/** 표: 담당자 최대 2명 표시, 나머지는 +N (호버로 전체) */
const ASSIGNEE_VISIBLE_MAX = 2;

const assigneeNames = (list: any[] | undefined): string[] =>
  (list || []).map((a) => String(a?.u_name || '').trim()).filter(Boolean);

const visibleAssigneeNames = (list: any[] | undefined): string[] =>
  assigneeNames(list).slice(0, ASSIGNEE_VISIBLE_MAX);

const hiddenAssigneeCount = (list: any[] | undefined): number =>
  Math.max(0, assigneeNames(list).length - ASSIGNEE_VISIBLE_MAX);

const formatAssigneeTitle = (list: any[] | undefined): string =>
  assigneeNames(list).join(', ');

const totalPages = computed(() => Math.ceil(totalCount.value / itemsPerPage.value));

// 멤버 목록 조회
const fetchMemberList = async () => {
  loading.value = true;
  try {
    // 기본 파라미터 (검색/필터/페이지네이션)
    const params: Record<string, any> = {
      search: searchText.value || undefined,
      bizGubun: selectedBizGubun.value || undefined,
      status: selectedStatus.value !== '' ? selectedStatus.value : undefined,
      page: currentPage.value,
      limit: itemsPerPage.value,
      ...(canSeeServiceTabs.value ? { serviceScope: serviceTab.value } : {})
    };
    
    // 대리 로그인 중일 때만 orgCode/roleLevel 전달 (백엔드가 JWT 관리자 검증 후 사용)
    // 일반 로그인 시에는 백엔드가 JWT에서 직접 orgCode/roleLevel을 사용
    if (store.getters.isImpersonating) {
      params.roleLevel = userRoleLevel.value;
      params.userIdx = currentUser.value?.idx;
    }
    
    const response = await memberApi.getList(params);

    if (response.data.success) {
      memberList.value = response.data.data;
      totalCount.value = response.data.pagination.total;
      if (response.data.tabCounts) {
        tabCounts.value = {
          service: Number(response.data.tabCounts.service) || 0,
          none: Number(response.data.tabCounts.none) || 0
        };
      }
    }
  } catch (error: any) {
    // 로그아웃·세션 만료(401) 시에는 안내하지 않음
    if (error?.response?.status === 401) return;
    console.error('멤버 목록 조회 오류:', error);
    store.dispatch('showError', '멤버 목록을 불러오는데 실패했습니다.');
  } finally {
    loading.value = false;
  }
};

// 사업구분 목록 조회 (색상 포함)
const fetchBizGubunList = async () => {
  try {
    const response = await memberApi.getBizGubunList();
    if (response.data.success) {
      bizGubunList.value = response.data.data;
      // 색상 맵 생성
      const colorMap: Record<string, string> = {};
      response.data.data.forEach((item: any) => {
        colorMap[item.bg_name] = item.bg_color;
      });
      bizGubunColorMap.value = colorMap;
    }
  } catch (error) {
    console.error('사업구분 목록 조회 오류:', error);
  }
};

const clearOrgFilter = () => {
  currentPage.value = 1;
  fetchMemberList();
};

const clearBizGubunFilter = () => {
  selectedBizGubun.value = '';
  currentPage.value = 1;
  fetchMemberList();
};

const clearStatusFilter = () => {
  selectedStatus.value = '';
  currentPage.value = 1;
  fetchMemberList();
};

const clearSearchFilter = () => {
  searchText.value = '';
  currentPage.value = 1;
  fetchMemberList();
};

const clearAllFilters = () => {
  selectedBizGubun.value = '';
  selectedStatus.value = '';
  searchText.value = '';
  currentPage.value = 1;
  fetchMemberList();
};

const onClearSearch = () => {
  searchText.value = '';
  fetchMemberList();
};

const setServiceTab = (tab: 'service' | 'none') => {
  if (serviceTab.value === tab) return;
  serviceTab.value = tab;
  currentPage.value = 1;
  fetchMemberList();
};

const onPageChange = () => fetchMemberList();
const onItemsPerPageChange = () => {
  currentPage.value = 1;
  fetchMemberList();
};

// 시설 상세 팝업 열기 (새 창 - 좌측상단, 화면 높이 전체)
const openMemberPopup = (member: any) => {
  const url = `/member/detail/${member.m_code}`;
  const width = 2000;
  const height = window.screen.height;
  const left = 0;
  const top = 0;
  window.open(url, 'memberDetailPopup', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
};

// 수정 다이얼로그 열기 (바로 편집 화면)
const openEditDialog = async (member: any) => {
  detailDialog.value = true;
  detailLoading.value = true;
  editForm.value = { m_name: member.m_name, m_status: member.m_status };
  try {
    const params = store.getters.isImpersonating
      ? { roleLevel: userRoleLevel.value, userIdx: currentUser.value?.idx }
      : {};
    const response = await memberApi.getById(member.m_idx, params);
    if (response.data.success) {
      selectedMember.value = response.data.data;
      editForm.value = { ...response.data.data };
      syncAssigneeIdsFromForm();
      // 담당자 체크박스 목록은 관리자만 로드
      if (isAdmin.value) {
        loadManagerList();
      } else {
        managerList.value = [];
      }
    }
  } catch (error: any) {
    detailDialog.value = false;
    if (error?.response?.status === 401) return;
    if (error.response?.status === 403) {
      store.dispatch('showError', '접근 권한이 없습니다.');
    } else {
      store.dispatch('showError', '멤버 정보를 불러오는데 실패했습니다.');
    }
  } finally {
    detailLoading.value = false;
  }
};

const closeEditDialog = () => {
  if (editLoading.value) return;
  detailDialog.value = false;
  selectedMember.value = null;
  editForm.value = {};
};

// 담당자 후보 목록 로드 (관리자 전체)
const loadManagerList = async () => {
  managerLoading.value = true;
  try {
    const response = await userApi.getManagers({
      orgCode: isAdmin.value ? undefined : userOrgCode.value,
      roleLevel: isAdmin.value ? 100 : userRoleLevel.value
    });
    if (response.data.success) {
      managerList.value = response.data.data;
    }
  } catch (error) {
    console.error('담당자 목록 조회 오류:', error);
  } finally {
    managerLoading.value = false;
  }
};

// 수정 저장 → 목록 갱신 후 창 닫기
const saveEdit = async () => {
  if (!editForm.value?.m_name?.trim()) {
    store.dispatch('showError', '시설명을 입력해주세요.');
    return;
  }
  editLoading.value = true;
  try {
    const updateData: Record<string, any> = {
      m_name: editForm.value.m_name,
      m_biz_gubun: editForm.value.m_biz_gubun,
      m_biz_num: editForm.value.m_biz_num,
      m_boss_name: editForm.value.m_boss_name,
      m_boss_phone: editForm.value.m_boss_phone,
      m_boss_email: editForm.value.m_boss_email,
      m_tell: editForm.value.m_tell,
      m_addr: editForm.value.m_addr,
      m_jungwon: editForm.value.m_jungwon,
      m_hyunwon: editForm.value.m_hyunwon,
    };
    // 활성/비활성·삭제는 관리자(90+)만
    if (canDeleteMember.value) {
      updateData.m_status = editForm.value.m_status;
    }

    if (isAdmin.value) {
      updateData.wage_assignee_ids = [...wageAssigneeIds.value];
      updateData.account_assignee_ids = [...accountAssigneeIds.value];
    }

    const response = await memberApi.update(editForm.value.m_code || editForm.value.m_idx, updateData);

    if (response.data.success) {
      store.dispatch('showSuccess', '저장되었습니다.');
      detailDialog.value = false;
      fetchMemberList();
    }
  } catch (error: any) {
    console.error('수정 오류:', error);
    const message = error.response?.data?.message || error.message || '저장에 실패했습니다.';
    store.dispatch('showError', message);
  } finally {
    editLoading.value = false;
  }
};

const getBizGubunColor = (bizGubun: string) => {
  return bizGubunColorMap.value[bizGubun] || '#9E9E9E';
};

// 사업구분 문자열 파싱 (콤마 구분)
const parseBizGubun = (bizGubunStr: string | null | undefined): string[] => {
  if (!bizGubunStr) return [];
  return bizGubunStr.split(',').map(s => s.trim()).filter(Boolean);
};

// 수정 모드: 선택된 사업구분 Set
const selectedBizGubunSet = computed(() => {
  return new Set(parseBizGubun(editForm.value?.m_biz_gubun));
});

// 현재 선택된 사업구분의 exclusive_group 조회
const getSelectedExclusiveGroup = (): string | null => {
  const selected = parseBizGubun(editForm.value?.m_biz_gubun);
  if (selected.length === 0) return null;
  
  const firstSelected = selected[0];
  const biz = bizGubunList.value.find(b => b.bg_name === firstSelected);
  return biz?.bg_exclusive_group || null;
};

// 특정 그룹이 비활성화되어야 하는지 확인
const isBizGubunDisabled = (exclusiveGroup: string | null): boolean => {
  const currentGroup = getSelectedExclusiveGroup();
  if (!currentGroup) return false;
  if (!exclusiveGroup) return false;
  return currentGroup !== exclusiveGroup;
};

// 사업구분 토글
const toggleBizGubun = (bizName: string, exclusiveGroup: string | null) => {
  const current = parseBizGubun(editForm.value?.m_biz_gubun);
  const idx = current.indexOf(bizName);
  
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    const currentGroup = getSelectedExclusiveGroup();
    if (currentGroup && currentGroup !== exclusiveGroup) {
      return;
    }
    current.push(bizName);
  }
  
  // DB 순서대로 정렬
  const orderedBizList = bizGubunList.value.map(b => b.bg_name);
  current.sort((a, b) => orderedBizList.indexOf(a) - orderedBizList.indexOf(b));
  
  editForm.value.m_biz_gubun = current.join(',');
};

const getCapacityPercent = (item: any) => {
  if (!item.m_jungwon) return 0;
  return Math.min(100, (item.m_hyunwon / item.m_jungwon) * 100);
};

const getCapacityClass = (item: any) => {
  const percent = getCapacityPercent(item);
  if (percent >= 90) return 'high';
  if (percent >= 70) return 'medium';
  return 'low';
};

const getRoleColor = (roleLevel: number) => {
  if (roleLevel >= 90) return 'red';
  if (roleLevel >= 60) return 'orange';
  if (roleLevel >= 50) return 'blue';
  return 'teal';
};

// 삭제 확인 — 관리자(90+)만
const confirmDelete = (member: any) => {
  if (!canDeleteMember.value) {
    store.dispatch('showError', '기관 삭제는 관리자만 가능합니다.');
    return;
  }
  deleteTarget.value = member;
  deleteDialog.value = true;
};

// 멤버 삭제
const deleteMember = async () => {
  if (!deleteTarget.value) return;
  if (!canDeleteMember.value) {
    store.dispatch('showError', '기관 삭제는 관리자만 가능합니다.');
    deleteDialog.value = false;
    return;
  }

  deleteLoading.value = true;
  try {
    await memberApi.delete(deleteTarget.value.m_code);
    store.dispatch('showSuccess', '탈퇴 처리되었습니다. 같은 정보로 다시 가입할 수 있습니다.');
    deleteDialog.value = false;
    deleteTarget.value = null;
    fetchMemberList();
  } catch (error: any) {
    console.error('멤버 삭제 오류:', error);
    store.dispatch('showError', error.response?.data?.message || '탈퇴 처리에 실패했습니다.');
  } finally {
    deleteLoading.value = false;
  }
};

// 대리 로그인 실행 (바로 접속)
const route = useRoute();

onMounted(() => {
  const querySearch = route.query.search;
  if (typeof querySearch === 'string' && querySearch.trim()) {
    searchText.value = querySearch.trim();
  }
  fetchMemberList();
  fetchBizGubunList();
});
</script>
